import { useCallback, useState } from 'react';

import { CellTypeNames, TextAlignment } from '../../../../common/table/CellTypeConstants';
import type { CellData, CellEditingSession } from '../../../../common/table/TableTypes';

import { getCellEditingAlignment, getCellTextAlignment } from '../utils/dataUtils/cell/tableCellDisplayUtils';
import { isFormula } from '../utils/dataUtils/formula/tableFormulaPredicateUtils';

type UseTableCellTextAlignmentArgs = {
    cellData: CellData | undefined;
    cellEditingSession: CellEditingSession | null;
    locale: string;
};

type UseTableCellTextAlignmentReturn = {
    textAlignment: TextAlignment | null;
    recomputeEditingTextAlignment: (editCellValue: string) => void;
};

type EditingTextAlignment = {
    sessionId: number;
    textAlignment: TextAlignment;
};

/**
 * Returns the cell alignment that must be available before the editor caret is measured.
 */
const getInitialTextAlignment = ({
    cellData,
    cellEditingSession,
    locale,
}: UseTableCellTextAlignmentArgs): TextAlignment | null => {
    if (!cellEditingSession || !cellData) return null;

    // For the editor only, ensure date types stay right aligned while editing.
    const isDateTimeString = !isFormula(cellData.value) && cellData.type?.name === CellTypeNames.DATE_TIME;
    return isDateTimeString ? TextAlignment.RIGHT : getCellTextAlignment(cellData, locale);
};

/**
 * Tracks the live text alignment shown by a table cell editor during an edit session.
 */
const useTableCellTextAlignment = ({
    cellData,
    cellEditingSession,
    locale,
}: UseTableCellTextAlignmentArgs): UseTableCellTextAlignmentReturn => {
    const initialTextAlignment = getInitialTextAlignment({ cellData, cellEditingSession, locale });
    const [editingTextAlignment, setEditingTextAlignment] = useState<EditingTextAlignment | null>(null);

    const textAlignment =
        editingTextAlignment && editingTextAlignment.sessionId === cellEditingSession?.sessionId
            ? editingTextAlignment.textAlignment
            : initialTextAlignment;

    const recomputeEditingTextAlignment = useCallback(
        (editCellValue: string) => {
            if (!cellEditingSession || !cellData) return;

            const updatedAlignment = getCellEditingAlignment(editCellValue, cellData, locale);
            if (!updatedAlignment) return;

            setEditingTextAlignment({
                sessionId: cellEditingSession.sessionId,
                textAlignment: updatedAlignment,
            });
        },
        [cellData, cellEditingSession, locale],
    );

    return {
        textAlignment,
        recomputeEditingTextAlignment,
    };
};

export default useTableCellTextAlignment;
